import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import useBookingStore from './booking';
import useInitStore from './init';
import type { StepOrderRequestData } from '../types/step-data';

const useFormOrderRequestStore = defineStore('form-order-request', () => {
  const bookingStore = useBookingStore();
  const initStore = useInitStore();

  const isMphOrderBlocked = ref<boolean>(false);
  const isProductOrderBlocked = ref<boolean>(false);
  const isOrderingForMph = ref<boolean>(false);
  const isOrderingProduct = ref<boolean>(false);
  const ne4OrderType = ref<string>('');
  const houseConnectionPrice = ref<number>(0);
  const houseConnectionLocation = ref<string>('');
  const iban = ref<string>('');
  const isBankTransferRequested = ref<boolean>(false);

  const stepData = computed<StepOrderRequestData>(() => ({
    blockMphBooking: isMphOrderBlocked.value,
    blockProductBooking: isProductOrderBlocked.value,
    bookingWithProduct: isOrderingProduct.value,
    connectionPoint: houseConnectionLocation.value,
    iban: iban.value,
    isMphOrder: isOrderingForMph.value,
    ne4Order: ne4OrderType.value,
    price: houseConnectionPrice.value,
    wantsBankTransfer: isBankTransferRequested.value,
  }));

  function setData(data: StepOrderRequestData) {
    isMphOrderBlocked.value = data.blockMphBooking;
    isProductOrderBlocked.value = data.blockProductBooking;
    isOrderingProduct.value = data.bookingWithProduct;
    houseConnectionLocation.value = data.connectionPoint;
    iban.value = data.iban;
    isOrderingForMph.value = data.isMphOrder;
    ne4OrderType.value = data.ne4Order;
    houseConnectionPrice.value = data.price;
    isBankTransferRequested.value = data.wantsBankTransfer;
  }

  function updateHouseConnectionPrice() {
    const { priceList } = initStore;

    if (isOrderingForMph.value) {
      houseConnectionPrice.value = bookingStore.isCurrentBookingModeAnyNe4Only
        ? 0
        : priceList!.priceMph;
    } else {
      houseConnectionPrice.value = isOrderingProduct.value
        ? priceList!.priceWithProduct
        : priceList!.priceWithoutProduct;
    }
  }

  if (bookingStore.isCurrentBookingModeAnyNe4Only) {
    isBankTransferRequested.value = true;
    isOrderingProduct.value = false;
    isProductOrderBlocked.value = initStore.bookingApproach?.isProductBookingPossible !== undefined
      ? !initStore.bookingApproach.isProductBookingPossible
      : false;
  }

  if (initStore.householdIsCompany || initStore.householdHasProductBooked) {
    isOrderingProduct.value = false;
    isProductOrderBlocked.value = true;
  }

  updateHouseConnectionPrice();

  return {
    isMphOrderBlocked,
    isProductOrderBlocked,
    isOrderingForMph,
    isOrderingProduct,
    ne4OrderType,
    houseConnectionPrice,
    houseConnectionLocation,
    iban,
    isBankTransferRequested,
    stepData,
    setData,
    updateHouseConnectionPrice,
  };
});

export default useFormOrderRequestStore;
